import moment from 'moment';
import {
    formatBubbleTime,
    isGroupedWithPrevious,
    isMessageSeenByOthers,
    renderDateSeparatorHtml,
    renderReplyQuoteHtml,
    shouldShowDateSeparator,
    stripHtmlForPreview,
} from './uiHelpers';

const REPLY_BTN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"><path d="M10 7.002V2.75a.748.748 0 0 0-1.272-.538l-8.5 8.25a.747.747 0 0 0 0 1.076l8.5 8.25c.217.21.539.269.814.153A.752.752 0 0 0 10 19.25V15h1.418c4.636 0 8.91 2.52 11.153 6.572l.021.038A.75.75 0 0 0 24 21.25c0-7.774-6.257-14.114-14-14.248z" fill="#a3dfff"/></svg>`;

export function getPreviousMessageMeta($container, selector = '.message') {
    const $messages = $container.find(selector);
    if (!$messages.length) {
        return null;
    }
    const $last = $messages.last();
    return {
        user_id: $last.data('sender'),
        sender_user_id: $last.data('sender-user-id'),
        date: $last.data('created-at'),
    };
}

export function getNextMessageMeta($container, selector = '.message') {
    const $messages = $container.find(selector);
    if (!$messages.length) {
        return null;
    }
    const $first = $messages.first();
    return {
        user_id: $first.data('sender'),
        sender_user_id: $first.data('sender-user-id'),
        date: $first.data('created-at'),
    };
}

export function maybeInsertDateSeparator($container, currentDate, prepend = false) {
    if (prepend) {
        const nextMeta = getNextMessageMeta($container);
        if (shouldShowDateSeparator(currentDate, nextMeta?.date)) {
            $container.prepend(renderDateSeparatorHtml(currentDate));
        }
    } else {
        const prevMeta = getPreviousMessageMeta($container);
        if (shouldShowDateSeparator(prevMeta?.date, currentDate)) {
            $container.append(renderDateSeparatorHtml(currentDate));
        }
    }
}

export function buildMessageBlockHtml({
    props,
    myMessage,
    safeMessage,
    normalTextLength,
    attachmentsHtml,
    imagesHtml,
    grouped,
    seen,
    showReplyButton,
    onReplyAttr,
}) {
    const userName =
        props.userName.length >= 30
            ? `${props.userName.substring(0, 30)}...`
            : props.userName;
    const timeLabel = formatBubbleTime(props.date);
    const seenLabel = myMessage && seen ? '  ✓ Seen' : '';
    const replyQuote = renderReplyQuoteHtml(props.reply, myMessage);
    const failedHtml = props.failedToSend
        ? `<div class="retry-message"><i class="fas fa-exclamation-circle"></i> We couldn't send the message, click to retry.</div>`
        : '';
    const replyBtn = showReplyButton
        ? `<div class="reply-message-side"><span class="reply-message-button" title="Reply">${REPLY_BTN_SVG}</span></div>`
        : '';
    const importanceHtml =
        props.importance && props.importance !== 'normal'
            ? `<span class="message-importance ${props.importance}">${props.importance.toUpperCase()}</span>`
            : '';

    return `
        <div class="message ${myMessage ? 'text-right' : 'text-left'}${grouped ? ' message-grouped' : ''}"
            ${props.id ? `id="message-${props.id}"` : ''}
            data-sender="${props.user_id ?? ''}"
            data-sender-user-id="${props.sender_user_id ?? 0}"
            data-created-at="${moment(props.date).toISOString()}">
            <div class="message-content content-wrapper">
                <div class="align-items-center d-flex message-name-row${grouped ? ' d-none' : ''}">
                    <h6 class="user-name">${userName}</h6>
                    ${importanceHtml}
                </div>
                <div class="message-body">
                    <div class="user-message ${myMessage ? 'user-message-right' : 'user-message-left'}"
                        ${props.failed_id ? `data-failed-id="${props.failed_id}"` : ''}>
                        ${replyQuote}
                        ${attachmentsHtml || ''}
                        ${imagesHtml || ''}
                        ${
                            safeMessage
                                ? `<div class="message-content${normalTextLength === 0 ? ' no-text' : ''}">${safeMessage}</div>`
                                : ''
                        }
                        ${failedHtml}
                    </div>
                    ${replyBtn}
                </div>
                <div class="message-header">
                    <span class="message-time">${timeLabel}${seenLabel}</span>
                </div>
            </div>
        </div>
    `;
}

export function computeGroupedAndSeen({
    prevMeta,
    props,
    myMessage,
    readBy,
    myUniqueId,
    myUserId,
    isLastMine,
}) {
    const grouped = isGroupedWithPrevious(prevMeta, {
        user_id: props.user_id,
        sender_user_id: props.sender_user_id,
        date: props.date,
    });
    const seen =
        myMessage &&
        isLastMine &&
        isMessageSeenByOthers(readBy, myUniqueId, myUserId);
    return { grouped, seen };
}

export function buildReplyPreview(message, formatLastMessage) {
    const preview = stripHtmlForPreview(
        formatLastMessage ? formatLastMessage(message.message, 100) : message.message
    );
    return {
        sender_name: message.sender_name,
        text: preview,
    };
}
